import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  Typography,
  Divider,
  Grid,
  useTheme,
  Paper,
  TableBody,
  Table,
  TableContainer,
  TableHead,
  TableCell,
  TableRow,
  TextField,
  MenuItem,
  IconButton,
  TableSortLabel,
  TablePagination,
  Autocomplete,
  Button,
} from "@mui/material";
import { ArrowUpward, ArrowDownward } from "@mui/icons-material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useLocation } from "react-router-dom";
import { GET, PUT, POST } from "../Generic/CrudRequest";
import Icons from "../../Utils/Icons";
import { LoadModel } from "./LoadModel";
import EncryptionUtils from "../../Utils/EncryptionUtils";
import Alerts from "../../Utils/Alerts";
import { useTranslation } from "react-i18next";



export default function DetailsLoads({ config }) {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loadData, setLoadData] = useState(new LoadModel());
  const [errors, setErrors] = useState({});
  const [client, setClient] = useState(0);
  const [clients, setClients] = useState([]);
  const [title, setTitle] = useState(t("loads.addOrEdit.add"));
  const [rowsUnits, setRowsUnits] = useState([]);
  const [totalRecords, setTotalRecords] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [orderBy, setOrderBy] = React.useState("");
  const [column, setColumn] = React.useState(0);
  const [order, setOrder] = React.useState("asc");
  const [locations, setLocations] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [locationsLoad, setLocationsLoad] = useState([]);
  const [locationLoad, setLocationLoad] = useState(null);
  const [truckLoad, setTruckLoad] = useState(null);
  const [searchVin, setSearchVin] = useState("");
  const [idUbicacionDestino, setIdUbicacionDestino] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [fecha, setFecha] = useState(dayjs(new Date()));
  dayjs.extend(utc);

  const columns = [
    { id: "vin", label: "VIN", minWidth: "100px" },
    { id: "modelo", label: t("loads.addOrEdit.model"), minWidth: "100px" },
    { id: "marca", label: t("loads.addOrEdit.brand"), minWidth: "100px" },
    { id: "destino", label: t("loads.addOrEdit.destination"), minWidth: "100px" },
  ];

  useEffect(() => {
    if (location.pathname.includes("/Cargas/Editar")) {
      setTitle(t("loads.addOrEdit.edit"));
      const loadId = getLoadIdFromPath(location.pathname);
      getLoadById(loadId);
    }

    fetchClients();
  }, [location.pathname]);


  useEffect(() => {
    fetchUnitsLoad();
  }, [page, rowsPerPage, column, order, client, locationLoad]);

  const getLoadIdFromPath = (pathname) => {
    const parts = pathname.split("/");
    return parts[parts.length - 1];
  };

  const getLoadById = async (loadId) => {
    const dataLoad = await GET(`${config.url}/api/Carriers/${loadId}`);
    const updateDataLoad = new LoadModel(dataLoad);

    setClient(updateDataLoad.idCliente);
    setFecha(dayjs.utc(updateDataLoad.fechaSolicitud).local());
    setLoadData(updateDataLoad);

    // Cargar datos de ubicación si existe
    if (updateDataLoad.idUbicacion !== 0) {
      try {
        const locationData = await GET(
          `${config.url}/api/Location/${updateDataLoad.idUbicacion}`
        );
        
        // Establecer directamente los datos de ubicación
        setLocationLoad({
          idUbicacion: locationData.idUbicacion,
          nombre: locationData.nombre,
          descripcion: locationData.descripcion || "",
          ciudad: locationData.ciudad || "",
          ubicacionTipo: locationData.ubicacionTipo || "",
        });

        // Cargar opciones de ubicación para el autocomplete
        fetchLocations(locationData.nombre, 1, 1);
      } catch (error) {
        console.error("Error loading location data:", error);
      }
    }

    // Cargar datos del truck si existe
    if (updateDataLoad.idTractocamion !== 0) {
      try {
        const truckData = await GET(
          `${config.url}/api/Truck/${updateDataLoad.idTractocamion}`
        );
        
        // Establecer directamente los datos del truck
        setTruckLoad({
          idTractocamion: truckData.idTractocamion,
          numeroEconomico: truckData.numeroEconomico,
        });

        // Cargar opciones de trucks para el autocomplete
        fetchTrucks(truckData.numeroEconomico, 1, 1);
      } catch (error) {
        console.error("Error loading truck data:", error);
      }
    }
  };

  const fetchClients = async () => {
    const clientsData = await GET(`${config.url}/api/Client`);
    setClients(clientsData);
  };

  const fetchUnitsLoad = async () => {
    const dataUnitsLoad = await GET(
      `${config.url}/api/Unit/paginado-unidades-carga`,
      {
        start: page * rowsPerPage,
        length: rowsPerPage,
        column: column,
        dir: order,
        vin: searchVin,
        idCliente: client,
        idUbicacion: locationLoad?.idUbicacion,
        idUbicacionDestiono: idUbicacionDestino,
      }
    );

    setRowsUnits(dataUnitsLoad.resultado);
    setTotalRecords(dataUnitsLoad.totalRegistros);
  };

  const fetchLocations = async (location, currentPage, perPage) => {
    const dataLocations = await GET(`${config.url}/api/Location/select`, {
      busqueda: location,
      page: currentPage,
      perPage: perPage,
    });

    setLocations(dataLocations.result);
  };

  const fetchTrucks = async (truck, currentPage, perPage) => {
    const dataTrucks = await GET(`${config.url}/api/Truck/GetSelect2Truck`, {
      busqueda: truck,
      page: currentPage,
      perPage: perPage,
    });

    setTrucks(dataTrucks.result);
  };

  const addLoad = async () => {
    const newErrors = {};

    if (loadData.idCliente === 0) {
      newErrors.cliente = t("loads.addOrEdit.requiredField");
    }

    if (loadData.idUbicacion === 0) {
      newErrors.ubicacion = t("loads.addOrEdit.requiredField");
    }

    if (
      loadData.fechaSolicitud === "Invalid Date" ||
      loadData.fechaSolicitud === ""
    ) {
      loadData.fechaSolicitud = "";
      newErrors.fechaSolicitud = t("loads.addOrEdit.requiredField");
    }

    if (loadData.noCarga === "") {
      newErrors.noCarga = t("loads.addOrEdit.requiredField");
    }

    if (loadData.idTractocamion === 0) {
      newErrors.truck = t("loads.addOrEdit.requiredField");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (loadData.unidades.length === 0) {
      handleShowAlert(t("loads.addOrEdit.unitsRequired"), "warning");
      return;
    }

    const dataUser = EncryptionUtils.getDecryptedData("user");
    const parsedUser = JSON.parse(dataUser);

    loadData.idUsuarioRegistro = parsedUser.userId;
    loadData.fechaSolicitud = fecha.format("MM-DD-YYYY HH:mm:ss");

    try {
      await POST(`${config.url}/api/Carriers`, loadData);
      handleShowAlert(t("loads.addOrEdit.saveSuccess"), "success");
      setTimeout(() => {
        navigate("/Cargas");
      }, 1500);
    } catch (error) {
      console.error("Error al guardar la carga:", error);
      handleShowAlert(t("loads.addOrEdit.saveError"), "error");
    }
  };

  const editLoad = async () => {
    const newErrors = {};

    if (loadData.idCliente === 0) {
      newErrors.cliente = t("loads.addOrEdit.requiredField");
    }

    if (loadData.idUbicacion === 0) {
      newErrors.ubicacion = t("loads.addOrEdit.requiredField");
    }

    if (
      loadData.fechaSolicitud === "Invalid Date" ||
      loadData.fechaSolicitud === ""
    ) {
      loadData.fechaSolicitud = "";
      newErrors.fechaSolicitud = t("loads.addOrEdit.requiredField");
    }

    if (loadData.noCarga === "") {
      newErrors.noCarga = t("loads.addOrEdit.requiredField");
    }

    if (loadData.idTractocamion === 0) {
      newErrors.truck = t("loads.addOrEdit.requiredField");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (loadData.unidades.length === 0) {
      handleShowAlert(t("loads.addOrEdit.unitsRequired"), "warning");
      return;
    }

    const dataUser = EncryptionUtils.getDecryptedData("user");
    const parsedUser = JSON.parse(dataUser);

    loadData.idUsuarioRegistro = parsedUser.userId;
    loadData.fechaSolicitud = fecha.format("MM-DD-YYYY HH:mm:ss");

    try {
      await PUT(`${config.url}/api/Carriers`, loadData);
      handleShowAlert(t("loads.addOrEdit.updateSuccess"), "success");
      setTimeout(() => {
        navigate("/Cargas");
      }, 1500);
    } catch (error) {
      console.error("Error al actualizar la carga:", error);
      handleShowAlert(t("loads.addOrEdit.updateError"), "error");
    }
  };

  const handleShowAlert = (message, type) => {
    setAlertMessage(message);
    setAlertType(type);
    setShowAlert(true);
  };

  const handleCloseAlert = () => {
    setShowAlert(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleRequestSort = (property, columnNumber) => {
    const isAsc = orderBy === property && order === "asc";
    setColumn(columnNumber);
    setOrderBy(property);
    setOrder(isAsc ? "desc" : "asc");
  };

  const handleChangeClient = (event) => {
    const value = event.target.value;
    setClient(value);
    setLoadData({
      ...loadData,
      idCliente: value,
    });
    // Limpiar error del campo cuando el usuario lo corrige
    if (errors.cliente) {
      setErrors(prev => ({ ...prev, cliente: undefined }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLoadData({
      ...loadData,
      [name]: value,
    });
    // Limpiar error del campo cuando el usuario lo corrige
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const updateUnitOrder = (updatedUnits) => {
    updatedUnits.forEach((unit, index) => {
      unit.orden = index + 1;
    });
  };

  const handleMoveDown = (index) => {
    if (index < loadData.unidades.length - 1) {
      const updatedUnidades = [...loadData.unidades];
      const temp = updatedUnidades[index];
      updatedUnidades[index] = updatedUnidades[index + 1];
      updatedUnidades[index + 1] = temp;
      updateUnitOrder(updatedUnidades);
      setLoadData({ ...loadData, unidades: updatedUnidades });
    }
  };

  const handleMoveUp = (index) => {
    if (index > 0) {
      const updatedUnidades = [...loadData.unidades];
      const temp = updatedUnidades[index];
      updatedUnidades[index] = updatedUnidades[index - 1];
      updatedUnidades[index - 1] = temp;
      updateUnitOrder(updatedUnidades);
      setLoadData({ ...loadData, unidades: updatedUnidades });
    }
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData("index", index.toString());
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, targetIndex) => {
    const draggedIndex = parseInt(e.dataTransfer.getData("index"));
    const unidades = [...loadData.unidades];
    const draggedUnit = unidades[draggedIndex];

    unidades.splice(draggedIndex, 1);
    unidades.splice(targetIndex, 0, draggedUnit);

    updateUnitOrder(unidades);
    setLoadData({ ...loadData, unidades: unidades });
  };

  const handleInputChange = (event, value) => {
    if (value.trim().length >= 3) {
      fetchLocations(value, 1, 10);
    } else {
      setLocations([]);
    }
  };

  const handleInputTruckChange = (event, value) => {
    if (value.trim().length >= 2) {
      fetchTrucks(value, 1, 10);
    } else {
      setTrucks([]);
    }
  };

  const handleSearch = () => {
    fetchUnitsLoad();
  };

  const handleClear = () => {
    setSearchVin("");
    setIdUbicacionDestino(null);
    setLocationsLoad([]);
  };

  const handleInputChangeLocations = (event, value) => {
    if (value.trim().length >= 3) {
      fetchLocations(value, 1, 10);
    } else {
      setLocationsLoad([]);
    }
  };

  const handleAddUnit = (row) => {
    const exists = loadData.unidades.some((unit) => unit.vin === row.vin);

    if (!exists) {
      const order = loadData.unidades.length + 1;

      const updatedUnidades = [...loadData.unidades, { ...row, orden: order }];
      setLoadData({ ...loadData, unidades: updatedUnidades });
    }
  };

  const handleRemoveUnit = (index) => {
    const updatedUnidades = [...loadData.unidades];
    updatedUnidades.splice(index, 1);

    updatedUnidades.forEach((unit, idx) => {
      unit.orden = idx + 1;
    });

    setLoadData({ ...loadData, unidades: updatedUnidades });
  };

  const handleSubmit = async () => {
    // Limpiar errores previos
    setErrors({});
    
    if (location.pathname.includes("/Cargas/Editar")) {
      await editLoad();
    } else {
      await addLoad();
    }
  };

  return (
    <>
      {showAlert && (
        <Alerts
          error={alertMessage}
          type={alertType}
          onClose={handleCloseAlert}
        />
      )}
      <Card
        variant="outlined"
        sx={{
          overflow: "hidden",
          boxShadow: "0 0 10px rgba(0, 0, 0, 0.2)",
          border: "1px solid rgba(0, 0, 0, 0.1)",
          borderRadius: 2,
        }}
      >
        <CardContent>
          <CardContent sx={{ display: "flex", alignItems: "center" }}>
            <Icons
              iconName="ArrowBack"
              fontSize="large"
              style={{
                backgroundColor: theme.palette.primary.main,
                color: "#fff",
                marginRight: 20,
              }}
              to="/Cargas"
              tooltip={t("loads.addOrEdit.back")}
            />
            <Typography variant="h3" sx={{ flex: "1", fontWeight: "bold" }}>
              {title} {t("loads.addOrEdit.title")}
            </Typography>
          </CardContent>
          <Divider sx={{ maxWidth: "calc(100% - 32px)", margin: "auto" }} />
          <Card
            variant="outlined"
            sx={{
              overflow: "hidden",
              boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
              border: "1px solid rgba(0, 0, 0, 0.1)",
              borderRadius: 2,
              maxWidth: "calc(100% - 32px)",
              margin: "auto",
              marginTop: 2,
            }}
          >
            <CardContent
              sx={{
                maxWidth: "calc(100% - 64px)",
                margin: "auto",
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  flex: "1",
                  color: theme.palette.primary.main,
                }}
              >
                {t("loads.addOrEdit.loadInfo")}
              </Typography>
              <Divider sx={{ maxWidth: "100%", marginTop: 2 }} />
              <Grid container spacing={2} sx={{ marginTop: 2 }}>
                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    label={t("loads.addOrEdit.client")}
                    name="idCliente"
                    variant="standard"
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                    value={client || 0}
                    onChange={handleChangeClient}
                    error={!!errors.cliente}
                    helperText={errors.cliente}
                  >
                    <MenuItem key={0} value={0}>
                      {t("loads.addOrEdit.select")}
                    </MenuItem>
                    {clients.map((cliente) => (
                      <MenuItem
                        key={cliente.idCliente}
                        value={cliente.idCliente}
                      >
                        {cliente.nombre}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    options={locations}
                    noOptionsText={t("noOptions")}
                    value={locationLoad}
                    getOptionLabel={(location) =>
                      `${location.nombre} ${location.descripcion} ${
                        location.ciudad ?? ""
                      } ${location.ubicacionTipo ?? ""}`
                    }
                    onChange={(event, value) => {
                      if (value) {
                        setLocationLoad(value);
                        setLoadData({
                          ...loadData,
                          ubicacion: value.nombre,
                          idUbicacion: value.idUbicacion,
                        });
                        // Limpiar error del campo cuando el usuario lo corrige
                        if (errors.ubicacion) {
                          setErrors(prev => ({ ...prev, ubicacion: undefined }));
                        }
                      } else {
                        setLocationLoad(null);
                        setLoadData({
                          ...loadData,
                          ubicacion: "",
                          idUbicacion: 0,
                        });
                      }
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option &&
                      value &&
                      option.idUbicacion === value.idUbicacion
                    }
                    onInputChange={handleInputChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("loads.addOrEdit.assemblyLocation")}
                        variant="standard"
                        fullWidth
                        value={locationLoad}
                        error={!!errors.ubicacion}
                        helperText={errors.ubicacion}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateTimePicker
                      label={t("loads.addOrEdit.requestDate")}
                      value={fecha}
                      onChange={(newValue) => {
                        setFecha(dayjs(newValue));
                        // Limpiar error del campo cuando el usuario lo corrige
                        if (errors.fechaSolicitud) {
                          setErrors(prev => ({ ...prev, fechaSolicitud: undefined }));
                        }
                      }}
                      slots={{
                        textField: (params) => (
                          <TextField
                            variant="standard"
                            fullWidth
                            {...params}
                            error={!!errors.fechaSolicitud}
                            helperText={errors.fechaSolicitud}
                          />
                        ),
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label={t("loads.addOrEdit.load")}
                    name="noCarga"
                    variant="standard"
                    fullWidth
                    onChange={handleChange}
                    value={loadData?.noCarga || ""}
                    error={!!errors.noCarga}
                    helperText={errors.noCarga}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    options={trucks}
                    noOptionsText={t("noOptions")}
                    value={truckLoad}
                    getOptionLabel={(truck) => `${truck.numeroEconomico}`}
                    onChange={(event, value) => {
                      if (value) {
                        setTruckLoad(value);
                        setLoadData({
                          ...loadData,
                          idTractocamion: value.idTractocamion,
                          numeroEconomico: value.numeroEconomico,
                        });
                        // Limpiar error del campo cuando el usuario lo corrige
                        if (errors.truck) {
                          setErrors(prev => ({ ...prev, truck: undefined }));
                        }
                      } else {
                        setTruckLoad(null);
                        setLoadData({
                          ...loadData,
                          numeroEconomico: "",
                          idTractocamion: 0,
                        });
                      }
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option &&
                      value &&
                      option.idTractocamion === value.idTractocamion
                    }
                    onInputChange={handleInputTruckChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("loads.addOrEdit.madrina")}
                        variant="standard"
                        fullWidth
                        value={truckLoad}
                        error={!!errors.truck}
                        helperText={errors.truck}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={1} sx={{ marginTop: 2 }}>
                <Grid item xs={12}>
                  <TextField
                    label={t("loads.addOrEdit.observations")}
                    name="observaciones"
                    variant="standard"
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                    value={loadData?.observaciones || ""}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          <Card
            variant="outlined"
            sx={{
              overflow: "hidden",
              boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
              border: "1px solid rgba(0, 0, 0, 0.1)",
              borderRadius: 2,
              maxWidth: "calc(100% - 32px)",
              margin: "auto",
              marginTop: 2,
            }}
          >
            <CardContent sx={{ maxWidth: "calc(100% - 64px)", margin: "auto" }}>
              <Typography
                variant="h5"
                sx={{
                  flex: "1",
                  color: theme.palette.primary.main,
                }}
              >
                {t("loads.addOrEdit.addedUnits")}
              </Typography>
              <Divider sx={{ maxWidth: "100%", marginTop: 2 }} />
              <Paper
                sx={{
                  width: "100%",
                  margin: "auto",
                  overflow: "hidden",
                  boxShadow: "none",
                  border: "none",
                }}
              >
                <TableContainer>
                  <Table stickyHeader aria-label="sticky table">
                    <TableHead>
                      <TableRow>
                        <TableCell style={{ fontWeight: "bold" }}>Orden</TableCell>
                        {columns.map((column) => (
                          <TableCell
                            key={column.id}
                            align="left"
                            style={{
                              minWidth: column.minWidth,
                              fontWeight: "bold",
                            }}
                          >
                            {column.label}
                          </TableCell>
                        ))}
                        <TableCell style={{ fontWeight: "bold" }}>Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loadData.unidades.length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={columns.length + 2}
                            align="center"
                          >
                            {t("loads.addOrEdit.noUnitsAvailable")}
                          </TableCell>
                        </TableRow>
                      )}
                      {loadData.unidades.map((unit, index) => {
                        return (
                          <TableRow key={index}>
                            <TableCell align="center">{unit.orden}</TableCell>
                            {columns.map((column) => (
                              <TableCell key={column.id} align="left">
                                {unit[column.id] && unit[column.id] !== "" ? (
                                  unit[column.id]
                                ) : (
                                  <i>{t("pagination.notAvailable")}</i>
                                )}
                              </TableCell>
                            ))}
                            <TableCell>
                              <Grid container spacing={1}>
                                <Grid item>
                                  <Icons
                                    iconName="Remove"
                                    fontSize="medium"
                                    style={{
                                      backgroundColor: theme.palette.danger.main,
                                      color: "#fff",
                                    }}
                                    onClick={() => handleRemoveUnit(index)}
                                    tooltip={t("buttons.remove")}
                                  />
                                </Grid>
                                <Grid item>
                                  <Icons
                                    iconName="ArrowUpward"
                                    fontSize="medium"
                                    style={{
                                      backgroundColor: theme.palette.primary.main,
                                      color: "#fff",
                                    }}
                                    onClick={() => handleMoveUp(index)}
                                    tooltip={t("loads.addOrEdit.moveUp")}
                                    disabled={index === 0}
                                  />
                                </Grid>
                                <Grid item>
                                  <Icons
                                    iconName="ArrowDownward"
                                    fontSize="medium"
                                    style={{
                                      backgroundColor: theme.palette.primary.main,
                                      color: "#fff",
                                    }}
                                    onClick={() => handleMoveDown(index)}
                                    tooltip={t("loads.addOrEdit.moveDown")}
                                    disabled={index === loadData.unidades.length - 1}
                                  />
                                </Grid>
                              </Grid>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </CardContent>
          </Card>
          <Card
            variant="outlined"
            sx={{
              overflow: "hidden",
              boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
              border: "1px solid rgba(0, 0, 0, 0.1)",
              borderRadius: 2,
              maxWidth: "calc(100% - 32px)",
              margin: "auto",
              marginTop: 2,
            }}
          >
            <CardContent sx={{ maxWidth: "calc(100% - 64px)", margin: "auto" }}>
              <Typography
                variant="h5"
                sx={{
                  flex: "1",
                  color: theme.palette.primary.main,
                }}
              >
                {t("loads.addOrEdit.unitsToAdd")}
              </Typography>
              <Divider sx={{ maxWidth: "100%", marginTop: 2 }} />
              <CardContent
                sx={{
                  maxWidth: "calc(100% - 32px)",
                  margin: "auto",
                  alignItems: "auto",
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="VIN"
                      type="search"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      variant="standard"
                      size="small"
                      value={searchVin}
                      onChange={(e) => setSearchVin(e.target.value)}
                      sx={{
                        width: 400,
                        maxWidth: "100%",
                        color: theme.palette.primary.main,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Autocomplete
                      options={locationsLoad}
                      noOptionsText={t("noOptions")}
                      getOptionLabel={(location) =>
                        `${location.nombre} ${location.descripcion} ${
                          location.ciudad ?? ""
                        } ${location.ubicacionTipo ?? ""}`
                      }
                      onChange={(event, value) => {
                        if (value) {
                          setIdUbicacionDestino(value.idUbicacion);
                        }
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option &&
                        value &&
                        option.idUbicacion === value.idUbicacion
                      }
                      onInputChange={handleInputChangeLocations}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t("loads.addOrEdit.location")}
                          variant="standard"
                          InputLabelProps={{
                            shrink: true,
                          }}
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Icons
                    iconName="Search"
                    fontSize="large"
                    onClick={handleSearch}
                    style={{ color: theme.palette.primary.main }}
                    tooltip={t("buttons.search")}
                  />
                  <Icons
                    iconName="Clear"
                    fontSize="large"
                    onClick={handleClear}
                    style={{ color: theme.palette.primary.main }}
                    tooltip={t("buttons.clear")}
                  />
                </Grid>
              </CardContent>
              <Paper
                sx={{
                  width: "100%",
                  margin: "auto",
                  overflow: "hidden",
                  boxShadow: "none",
                  border: "none",
                }}
              >
                <TableContainer>
                  <Table stickyHeader aria-label="sticky table">
                    <TableHead>
                      <TableRow>
                        {columns.map((column) => (
                          <TableCell
                            key={column.id}
                            align="left"
                            style={{
                              minWidth: column.minWidth,
                              fontWeight: "bold",
                            }}
                          >
                            <TableSortLabel
                              active={orderBy === column.id}
                              direction={orderBy === column.id ? order : "asc"}
                              onClick={() =>
                                handleRequestSort(
                                  column.id,
                                  columns.indexOf(column)
                                )
                              }
                            >
                              {column.label}
                            </TableSortLabel>
                          </TableCell>
                        ))}
                        <TableCell style={{ fontWeight: "bold" }}>Acción</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rowsUnits.length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={columns.length + 1}
                            align="center"
                          >
                            {t("loads.addOrEdit.noUnitsAvailable")}
                          </TableCell>
                        </TableRow>
                      )}
                      {rowsUnits.map((row, index) => {
                        // Verificar si la unidad ya está agregada
                        const isAlreadyAdded = loadData.unidades.some(
                          (unit) => unit.vin === row.vin
                        );
                        
                        return (
                          <TableRow key={index}>
                            {columns.map((column) => (
                              <TableCell key={column.id} align="left">
                                {row[column.id] && row[column.id] !== "" ? (
                                  row[column.id]
                                ) : (
                                  <i>{t("pagination.notAvailable")}</i>
                                )}
                              </TableCell>
                            ))}
                            <TableCell>
                              {isAlreadyAdded ? (
                                <Icons
                                  iconName="Check"
                                  fontSize="medium"
                                  style={{
                                    backgroundColor: theme.palette.success.main,
                                    color: "#fff",
                                  }}
                                  tooltip="Unidad ya agregada"
                                />
                              ) : (
                                <Icons
                                  iconName="Add"
                                  fontSize="medium"
                                  style={{
                                    backgroundColor: theme.palette.primary.main,
                                    color: "#fff",
                                  }}
                                  onClick={() => handleAddUnit(row)}
                                  tooltip={t("buttons.add")}
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 100]}
                  component="div"
                  count={totalRecords}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage={t("pagination.labelRowsPerPage")}
                  labelDisplayedRows={({ from, to, count }) =>
                    t("pagination.displayedRows", { from, to, count })
                  }
                />
              </Paper>
            </CardContent>
          </Card>
          <Grid
            container
            justifyContent="flex-end"
            sx={{
              marginTop: 2,
              marginLeft: "-16px",
            }}
          >
            <Button variant="contained" color="primary" onClick={handleSubmit}>
              {t("loads.addOrEdit.save")}
            </Button>
          </Grid>
        </CardContent>
      </Card>
    </>
  );
}
